import HttpClient from "./http.client";
import { apiConfig } from "@/config/api.config";
import type { RoomBookingScheduleResponse } from "@/types/api.types";

// Initialize HTTP client for Bookings service
const bookingsClient = new HttpClient(apiConfig.bookings.baseUrl);

// Booking interfaces
export interface Booking {
  id: string;
  userId: string;
  hotelId: string;
  roomId: string;
  checkInDate: string;
  checkOutDate: string;
  numberOfGuests: number;
  totalAmount: number;
  currency: string;
  status: "pending" | "confirmed" | "cancelled" | "completed" | "refunded";
  paymentStatus: "pending" | "paid" | "failed" | "refunded";
  paymentMethod?: string;
  specialRequests?: string;
  guestDetails: GuestDetails;
  createdAt: string;
  updatedAt: string;
}

export interface GuestDetails {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  address?: string;
  city?: string;
  country?: string;
}

export interface CreateBookingData {
  hotelId: string;
  roomId: string;
  checkInDate: string;
  checkOutDate: string;
  numberOfGuests: number;
  guestDetails: GuestDetails;
  specialRequests?: string;
}

export interface BookingFilters {
  status?: string;
  startDate?: string;
  endDate?: string;
  hotelId?: string;
  roomId?: string;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
}

export interface InitiateTransactionData {
  fullName: string;
  phoneNumber: string;
  email: string;
  checkInDate: string;
  checkOutDate: string;
  roomId: string;
  isSelf: boolean;
  numberOfGuests: number;
}

export interface InitiateTransactionResult {
  status: string;
  message: string;
  data: {
    link: string;
  };
}

export interface InitiateTransactionResponse {
  data: InitiateTransactionResult;
  status: string;
  statusCode: string;
  message: string;
}

export interface VerifyTransactionResult {
  status?: string;
  [key: string]: any;
}

export interface VerifyTransactionResponse {
  data: VerifyTransactionResult;
  status: string;
  statusCode: string;
  message: string;
}

export interface UserBookingRoom {
  id: string;
  name: string;
  roomNumber: string;
  description: string;
  price: number;
  roomType: string;
}

export interface UserBookingHotel {
  name: string;
  address: string;
}

export interface UserBooking {
  id: string;
  dateCreated: string;
  userId: string;
  fullName: string;
  checkInDate: string;
  checkOutDate: string;
  roomId: string;
  room: UserBookingRoom;
  hotelId: string;
  hotel: UserBookingHotel;
  days: number;
  amount: number;
  isCheckedIn: boolean;
  channelDesc: string;
  status: string;
}

export interface Pagination {
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
}

export interface UserBookingsResult {
  items: UserBooking[];
  pagination: Pagination;
}

export interface UserBookingsResponse {
  data: UserBookingsResult;
  status: string;
  statusCode: string;
  message: string;
}

// Bookings Service API calls
export const bookingsService = {
  // Create booking
  createBooking: async (data: CreateBookingData) => {
    return await bookingsClient.post<Booking>("bookings", data);
  },

  // Get user's bookings
  getMyBookings: async (params?: PaginationParams & BookingFilters) => {
    return await bookingsClient.get<{ bookings: Booking[]; total: number }>(
      "bookings/my-bookings",
      { params }
    );
  },

  // Get specific booking
  getBookingById: async (bookingId: string) => {
    return await bookingsClient.get<Booking>(`bookings/${bookingId}`);
  },

  // Update booking
  updateBooking: async (
    bookingId: string,
    data: Partial<CreateBookingData>
  ) => {
    return await bookingsClient.put<Booking>(`bookings/${bookingId}`, data);
  },

  // Cancel booking
  cancelBooking: async (bookingId: string, reason?: string) => {
    return await bookingsClient.post<Booking>(`bookings/${bookingId}/cancel`, {
      reason,
    });
  },

  // Confirm booking
  confirmBooking: async (bookingId: string) => {
    return await bookingsClient.post<Booking>(`bookings/${bookingId}/confirm`);
  },

  // Check availability
  checkAvailability: async (
    roomId: string,
    checkIn: string,
    checkOut: string
  ) => {
    return await bookingsClient.post<{
      available: boolean;
      conflictingBookings?: Booking[];
    }>("bookings/check-availability", { roomId, checkIn, checkOut });
  },

  // Get booking price
  calculateBookingPrice: async (
    roomId: string,
    checkIn: string,
    checkOut: string,
    guests: number
  ) => {
    return await bookingsClient.post<{
      basePrice: number;
      taxes: number;
      fees: number;
      total: number;
      currency: string;
      nights: number;
    }>("bookings/calculate-price", {
      roomId,
      checkIn,
      checkOut,
      guests,
    });
  },

  // Payment related
  processPayment: async (bookingId: string, paymentData: any) => {
    return await bookingsClient.post(
      `bookings/${bookingId}/payment`,
      paymentData
    );
  },

  verifyPayment: async (bookingId: string, reference: string) => {
    return await bookingsClient.post(`bookings/${bookingId}/verify-payment`, {
      reference,
    });
  },

  // Get booking statistics
  getBookingStats: async () => {
    return await bookingsClient.get("bookings/stats");
  },

  // Add review after booking
  addBookingReview: async (
    bookingId: string,
    rating: number,
    comment: string
  ) => {
    return await bookingsClient.post(`bookings/${bookingId}/review`, {
      rating,
      comment,
    });
  },

  // Get room booking schedule
  getRoomBookingSchedule: async (roomId: string) => {
    return await bookingsClient.get<RoomBookingScheduleResponse>(
      `Rooms/v1/room-booking-schedule`,
      { params: { roomId } }
    );
  },

  // Get apartment booking schedule (uses apartmentId which is the property ID)
  getApartmentBookingSchedule: async (apartmentId: string) => {
    return await bookingsClient.get<RoomBookingScheduleResponse>(
      `Rooms/v1/room-booking-schedule`,
      { params: { roomId: apartmentId } }
    );
  },

  // Initiate a room booking transaction
  initiateTransaction: async (data: InitiateTransactionData) => {
    return await bookingsClient.post<InitiateTransactionResponse>(
      "Transaction/v1/initiate-transaction",
      data
    );
  },

  // Verify a booking transaction by its tx_ref
  verifyTransaction: async (txRef: string) => {
    return await bookingsClient.get<VerifyTransactionResponse>(
      `Transaction/v1/verify-transaction/${txRef}`
    );
  },

  // Get the logged-in user's bookings
  getUserBookings: async (pageNumber = 1, pageSize = 10) => {
    return await bookingsClient.get<UserBookingsResponse>(
      "Booking/v1/user-bookings",
      { params: { PageNumber: pageNumber, PageSize: pageSize } }
    );
  },
};

export default bookingsService;
